import { useLiveQuery } from 'dexie-react-hooks';
import { useCallback, useMemo, useState } from 'react';

import {
    amountToCents,
    centsToAmount,
    findingMutations,
    hasCostOverride,
    manualUrgency,
    repairCostFields,
    totalCents,
    type NhgClassification,
    type Urgency,
} from '@/entities/finding';
import {
    InspectionLockedError,
    inspectionApi,
    inspectionMutations,
    isSubmitted,
    type AnswerFields,
} from '@/entities/inspection';
import { isApiError, useSessionStore } from '@/shared/api';
import { usePreference } from '@/shared/lib';
import { photoMutations } from '@/entities/photo';
import { enginePackageFor } from '@/entities/template';
import {
    acknowledgeConflicts,
    isLostConnection,
    isStorageFull,
    newUlid,
    useTenantDb,
    type AnswerRecord,
    type ConflictRecord,
    type FindingRecord,
    type InspectionRecord,
    type MultiCountRecord,
    type OrderRecord,
    type PhotoRecord,
    type TenantDatabase,
    useIsPreparing,
} from '@/shared/offline';

import { surveyCopy, surveyErrors } from '../copy';
import type { ManualFindingDraft } from '../components/ManualFindingForm';
import { completionReport } from '../model/completion';
import {
    buildGraph,
    computeSnapshot,
    type AnswerKey,
    type NodeId,
    type PackageQuestion,
    type TemplateGraph,
    type TemplatePackage,
} from '../engine';
import {
    answerRecordsByKey,
    answerSetFrom,
    overridesByKey,
    notesByKey,
    photosByFinding,
    photosByKey,
} from '../model/records';
import {
    collectFindings,
    costBlocks,
    manualFindingView,
    nextAnswer,
    parseNumberInput,
    placeOf,
    treeRows,
    type FindingView,
    type QuestionFinding,
    type QuestionView,
} from '../model/view';
import {
    firstAskingMainNodeId,
    mainNodeIdOf,
    neighbourMainNodeId,
    partMeta,
    partPosition,
    partStream,
    planPartSweep,
    type StreamEntry,
} from '../model/stream';
import { answerKeyToWire, multiCountKeyToWire, nodeAddressToWire } from '../model/wire-keys';

/**
 * De brug tussen Dexie, de engine en het scherm.
 *
 * Alles wat het scherm toont komt uit de database van de actieve omgeving en
 * uit de engine, die exact dezelfde regels rekent als de API. Alles wat de
 * inspecteur doet gaat via `applyMutation` (lokaal schrijven plus outbox in
 * één transactie, in de vorm van het draadformaat).
 */

export interface SurveyData {
    db: TenantDatabase;
    inspection: InspectionRecord;
    order: OrderRecord | undefined;
    graph: TemplateGraph;
    answers: readonly AnswerRecord[];
    multiCounts: readonly MultiCountRecord[];
    photos: readonly PhotoRecord[];
    conflicts: readonly ConflictRecord[];
    /** De bevindingen die de inspecteur zelf toevoegde (`source = manual`). */
    manualFindings: readonly FindingRecord[];
    /** Wat van deze opname nog naar de server moet: bewerkingen in de outbox en foto's. */
    pending: { operations: number; photos: number };
}

export type SurveyLoad =
    | { status: 'loading' }
    | { status: 'no-environment' }
    | { status: 'impersonating' }
    | { status: 'not-found' }
    /** Net gestart en nog onderweg naar dit apparaat: zie `prepareSurvey`. */
    | { status: 'preparing' }
    | { status: 'no-questionnaire' }
    | { status: 'package-pending' }
    | { status: 'package-invalid' }
    | { status: 'ready'; data: SurveyData };

const graphs = new WeakMap<TemplatePackage, TemplateGraph>();

/** Eén graaf per pakket: `buildGraph` op 7.304 randen hoort niet bij elke render. */
function graphFor(pkg: TemplatePackage): TemplateGraph {
    let graph = graphs.get(pkg);

    if (!graph) {
        graph = buildGraph(pkg);
        graphs.set(pkg, graph);
    }

    return graph;
}

/**
 * Leest de opname uit de database van de actieve omgeving. Elke tabel heeft
 * een eigen live query: een tik op een antwoord leest het pakket van 4,6 MB
 * dus niet opnieuw.
 */
export function useSurveyData(inspectionUuid: string): SurveyLoad {
    const db = useTenantDb();
    /* Tijdens het meekijken is er bewust geen lokale database: zie `tenantScopeOf`. */
    const impersonating = useSessionStore((state) => state.impersonation !== null);
    const preparing = useIsPreparing(inspectionUuid);

    const inspection = useLiveQuery(
        async () => (db ? { value: await db.inspections.get(inspectionUuid) } : null),
        [db, inspectionUuid],
    );
    const orderUuid = inspection?.value?.orderUuid ?? null;
    const versionUuid = inspection?.value?.templateVersionUuid ?? null;

    const order = useLiveQuery(
        async () => ({ value: db && orderUuid ? await db.orders.get(orderUuid) : undefined }),
        [db, orderUuid],
    );
    const version = useLiveQuery(
        async () => ({
            value: db && versionUuid ? await db.templateVersions.get(versionUuid) : undefined,
        }),
        [db, versionUuid],
    );
    const document = useLiveQuery(
        async () => ({
            value: db && versionUuid ? await db.packageDocuments.get(versionUuid) : undefined,
        }),
        [db, versionUuid],
    );
    const answers = useLiveQuery(
        () => db?.answers.where('inspectionUuid').equals(inspectionUuid).toArray() ?? [],
        [db, inspectionUuid],
    );
    const multiCounts = useLiveQuery(
        () => db?.multiCounts.where('inspectionUuid').equals(inspectionUuid).toArray() ?? [],
        [db, inspectionUuid],
    );
    const photos = useLiveQuery(
        () => db?.photos.where('inspectionUuid').equals(inspectionUuid).toArray() ?? [],
        [db, inspectionUuid],
    );
    const manualFindings = useLiveQuery(
        () =>
            db?.findings
                .where('inspectionUuid')
                .equals(inspectionUuid)
                .filter((finding) => finding.source === 'manual')
                .toArray() ?? [],
        [db, inspectionUuid],
    );
    const pending = useLiveQuery(
        async () =>
            db
                ? {
                      operations: await db.outbox
                          .where('inspectionUuid')
                          .equals(inspectionUuid)
                          .count(),
                      photos: await db.photos
                          .where('inspectionUuid')
                          .equals(inspectionUuid)
                          .filter(
                              (photo) =>
                                  photo.deleted === 1 ||
                                  photo.state === 'pending' ||
                                  photo.state === 'uploading',
                          )
                          .count(),
                  }
                : { operations: 0, photos: 0 },
        [db, inspectionUuid],
    );
    const conflicts = useLiveQuery(
        () =>
            db?.conflicts
                .where('inspectionUuid')
                .equals(inspectionUuid)
                .filter((conflict) => conflict.acknowledged === 0)
                .toArray() ?? [],
        [db, inspectionUuid],
    );

    const pkg = useMemo(
        () => (version && document ? enginePackageFor(version.value, document.value) : null),
        [version, document],
    );

    if (!db) {
        return impersonating ? { status: 'impersonating' } : { status: 'no-environment' };
    }

    if (inspection === undefined || inspection === null) {
        return { status: 'loading' };
    }

    if (!inspection.value) {
        return preparing ? { status: 'preparing' } : { status: 'not-found' };
    }

    if (inspection.value.templateVersionUuid === null) {
        return { status: 'no-questionnaire' };
    }

    if (version === undefined || document === undefined || order === undefined) {
        return { status: 'loading' };
    }

    if (!pkg || pkg.status === 'missing') {
        return preparing ? { status: 'preparing' } : { status: 'package-pending' };
    }

    if (pkg.status === 'invalid') {
        return { status: 'package-invalid' };
    }

    if (!answers || !multiCounts || !photos || !conflicts || !pending || !manualFindings) {
        return { status: 'loading' };
    }

    return {
        status: 'ready',
        data: {
            db,
            inspection: inspection.value,
            order: order.value,
            graph: graphFor(pkg.pkg),
            answers,
            multiCounts,
            photos,
            conflicts,
            manualFindings,
            pending,
        },
    };
}

export interface SurveyObject {
    orderNumber: string;
    norm: string;
    address: string;
    /** "NTA 8060 · 24-1183 · BOUWTECHNISCHE KEURING" */
    eyebrow: string;
}

/** `nta8060` → `NTA 8060`. */
export function normLabel(code: string): string {
    const match = /^([a-z]+)\s*(\d+)$/i.exec(code.trim());

    return match ? `${match[1]?.toUpperCase()} ${match[2]}` : code.toUpperCase();
}

/**
 * "NTA 8060 · 24-1183 · BOUWTECHNISCHE KEURING": de norm, het nummer en de dienst.
 * Noemt de dienst de norm al ("Bouwtechnische keuring (NTA 8060)"), dan staat de
 * norm er niet nog eens voor.
 */
export function objectEyebrow(norm: string, orderNumber: string, service: string): string {
    const compact = (text: string) => text.replace(/\s+/g, '').toLowerCase();
    const repeatsNorm = norm !== '' && compact(service).includes(compact(norm));

    return [repeatsNorm ? '' : norm, orderNumber, service]
        .filter((part) => part.length > 0)
        .join(' · ');
}

export function addressLine(order: OrderRecord | undefined): string {
    const address = order?.address;

    if (!address) {
        return '';
    }

    const street = [
        address.street,
        `${address.houseNumber ?? ''}${address.houseNumberSuffix ?? ''}`,
    ]
        .filter((part) => part && part.trim() !== '')
        .join(' ');

    return [street, address.city].filter(Boolean).join(', ');
}

export function useSurveyState(data: SurveyData) {
    const { db, inspection, order, graph } = data;
    const inspectionUuid = inspection.uuid;
    /*
     * Het onderdeel waar de inspecteur is, bewaard op dit apparaat: na herladen - de
     * app op de achtergrond die de iPad opruimde, of een nieuwe versie - gaat
     * "Opname hervatten" daar verder in plaats van bij het eerste onderdeel.
     *
     * Het scherm volgt zijn eigen state en niet de voorkeur: een voorkeur meldt
     * zich pas nadat het scherm zich op die store heeft ingeschreven, en een tik
     * die daar net voor komt, kwam dan pas bij de volgende render aan. De voorkeur
     * is de spiegel op het apparaat; wat je tikt, staat meteen op het scherm.
     */
    const [storedNodeId, rememberNodeId] = usePreference(`opname-onderdeel:${inspectionUuid}`, '');
    const [pickedNodeId, setPickedNodeId] = useState<NodeId | null>(
        storedNodeId === '' ? null : storedNodeId,
    );
    const setCurrentNodeId = useCallback(
        (nodeId: NodeId) => {
            setPickedNodeId(nodeId);
            rememberNodeId(nodeId);
        },
        [rememberNodeId],
    );
    const currentNodeId = pickedNodeId;
    const [writeError, setWriteError] = useState<string | null>(null);

    /**
     * Een mislukte lokale schrijfactie mag het scherm nooit wegvagen: de
     * inspecteur staat in een kruipruimte en zijn antwoorden moeten blijven
     * staan. De fout wordt gemeld, niet gegooid.
     */
    const guard = useCallback(async (label: string, action: () => Promise<unknown>) => {
        try {
            await action();
            setWriteError(null);
        } catch (cause) {
            if (cause instanceof InspectionLockedError) {
                setWriteError(surveyErrors.locked);

                return;
            }

            console.error('[opname] lokaal opslaan mislukt', cause);
            setWriteError(
                isStorageFull(cause)
                    ? surveyErrors.storageFull
                    : isLostConnection(cause)
                      ? surveyErrors.storageLost
                      : label,
            );
        }
    }, []);

    const answers = useMemo(
        () => answerSetFrom(data.answers, data.multiCounts),
        [data.answers, data.multiCounts],
    );
    const snapshot = useMemo(() => computeSnapshot(graph, answers), [graph, answers]);
    const records = useMemo(() => answerRecordsByKey(data.answers), [data.answers]);
    const notes = useMemo(() => notesByKey(records), [records]);
    const overrides = useMemo(() => overridesByKey(records), [records]);
    const photosByQuestion = useMemo(() => photosByKey(data.photos), [data.photos]);
    const findingPhotos = useMemo(() => photosByFinding(data.photos), [data.photos]);

    const manualFindingViews = useMemo(
        () => data.manualFindings.map((record) => manualFindingView(snapshot, record)),
        [snapshot, data.manualFindings],
    );
    const findings = useMemo(
        () => [...collectFindings(snapshot, overrides), ...manualFindingViews],
        [snapshot, overrides, manualFindingViews],
    );
    /* Alle bevindingen in het paneel tellen exact op tot deze blokken. */
    const costs = useMemo(
        () => costBlocks(snapshot.costs.total, manualFindingViews),
        [snapshot, manualFindingViews],
    );

    /**
     * Per antwoord wat het detailblok toont: de bevinding die de overschrijvingen
     * draagt (de eerste in de boom). Een antwoord waarvan de inspecteur de
     * bedragen overschreef, houdt zijn blok ook als het daardoor geen bevinding
     * meer is - anders kan hij het niet terugzetten.
     */
    const questionFindings = useMemo(() => {
        const map = new Map<AnswerKey, QuestionFinding>();

        for (const finding of findings) {
            if (finding.answerKey === null || map.has(finding.answerKey)) {
                continue;
            }

            const override = overrides.get(finding.answerKey);

            map.set(finding.answerKey, {
                amountCents: totalCents(finding.costs),
                urgency: finding.urgency,
                nhg: override?.nhgClassification ?? null,
                location: override?.location ?? '',
                ownAmount: override !== undefined && hasCostOverride(override),
            });
        }

        for (const [key, override] of overrides) {
            if (!map.has(key) && hasCostOverride(override)) {
                map.set(key, {
                    amountCents:
                        (amountToCents(override.directCost) ?? 0) +
                        (amountToCents(override.termCost) ?? 0) +
                        (amountToCents(override.improvementCost) ?? 0),
                    urgency: null,
                    nhg: override.nhgClassification,
                    location: override.location ?? '',
                    ownAmount: true,
                });
            }
        }

        return map;
    }, [findings, overrides]);
    const rows = useMemo(() => treeRows(snapshot), [snapshot]);

    /*
     * Waar de inspecteur is. `activeNodeId` is het onderdeel dat hij koos - een
     * subonderdeel mag dat zijn; `mainNodeId` is het hoofdonderdeel eromheen, en
     * dat is wat het scherm toont: alles van Gevels achter elkaar, tot de knop
     * naar het volgende hoofdonderdeel.
     */
    const activeNodeId =
        currentNodeId !== null && snapshot.tree.nodes.has(currentNodeId)
            ? currentNodeId
            : firstAskingMainNodeId(snapshot);
    const mainNodeId = mainNodeIdOf(snapshot, activeNodeId);
    const activeRuntime = activeNodeId ? snapshot.tree.nodes.get(activeNodeId) : undefined;
    const activeProgress = activeNodeId ? snapshot.progress.nodes.get(activeNodeId) : undefined;
    const mainRuntime = mainNodeId ? snapshot.tree.nodes.get(mainNodeId) : undefined;

    const stream = useMemo<StreamEntry[]>(
        () => partStream(snapshot, mainNodeId),
        [snapshot, mainNodeId],
    );
    const meta = useMemo(
        () => partMeta(snapshot, mainNodeId, stream),
        [snapshot, mainNodeId, stream],
    );
    const position = useMemo(() => partPosition(snapshot, mainNodeId), [snapshot, mainNodeId]);

    /** "Geen gebreken hier" vult het hele hoofdonderdeel, niet alleen de knoop eronder. */
    const sweepPlan = useMemo(
        () => planPartSweep(graph, snapshot, answers, mainNodeId),
        [graph, snapshot, answers, mainNodeId],
    );

    const object = useMemo<SurveyObject>(() => {
        const norm = normLabel(graph.package.inspection_type);
        const orderNumber = order?.number ?? '';

        return {
            orderNumber,
            norm,
            address: addressLine(order) || graph.package.name,
            eyebrow: objectEyebrow(norm, orderNumber, order?.serviceLabel ?? graph.package.name),
        };
    }, [graph, order]);

    /** Het bouwjaar uit de BAG: een hint bij de bouwperiodevraag, nooit een antwoord. */
    const buildYear = order?.address?.bagBuildYear ?? null;

    const selectNode = useCallback(
        (nodeId: NodeId) => setCurrentNodeId(nodeId),
        [setCurrentNodeId],
    );

    /** Vorig of volgend hoofdonderdeel: binnen één onderdeel scrol je gewoon door. */
    const goToNeighbour = useCallback(
        (direction: -1 | 1) => {
            const next = neighbourMainNodeId(snapshot, mainNodeId, direction);

            if (next) {
                setCurrentNodeId(next);
            }
        },
        [snapshot, mainNodeId, setCurrentNodeId],
    );

    const goToFirstIncomplete = useCallback(() => {
        const target = snapshot.progress.firstIncompleteNodeId;

        if (target) {
            setCurrentNodeId(target);
        }
    }, [snapshot, setCurrentNodeId]);

    const saveFields = useCallback(
        (key: AnswerKey, fields: Partial<AnswerFields>) =>
            inspectionMutations.saveAnswer({ inspectionUuid, ...answerKeyToWire(key), fields }, db),
        [db, inspectionUuid],
    );

    const clear = useCallback(
        (key: AnswerKey) =>
            inspectionMutations.clearAnswer({ inspectionUuid, ...answerKeyToWire(key) }, db),
        [db, inspectionUuid],
    );

    /**
     * Een tik op een optie. `selected` is wat de rij op het scherm zou worden;
     * het nieuwe antwoord bouwt voort op wat er op dat moment bewaard staat, zodat
     * een tik die sneller komt dan het scherm niets terugdraait.
     */
    const toggleOption = useCallback(
        async (view: QuestionView, optionId: number, selected: boolean) => {
            await guard(surveyErrors.answer, () =>
                inspectionMutations.updateAnswer(
                    { inspectionUuid, ...answerKeyToWire(view.key) },
                    (current) => {
                        const next = nextAnswer(
                            view.question,
                            current ? { option_ids: current.optionIds } : undefined,
                            optionId,
                            selected,
                        );
                        const optionIds = next.option_ids ?? [];

                        return optionIds.length === 0 ? null : { optionIds: [...optionIds] };
                    },
                    db,
                ),
            );
        },
        [inspectionUuid, db, guard],
    );

    /** Getal- en tekstvragen. Een leeg veld wist het antwoord. */
    const setValue = useCallback(
        async (view: QuestionView, raw: string) => {
            const text = raw.trim();

            await guard(surveyErrors.answer, () => {
                if (text === '') {
                    return clear(view.key);
                }

                if (view.question.type === 'number') {
                    const value = parseNumberInput(text);

                    return value === null
                        ? Promise.resolve()
                        : saveFields(view.key, { valueNumber: value });
                }

                /* Zoals de server hem bewaart, zodat de sync het veld niet onder het typen terugzet. */
                return saveFields(view.key, { valueText: text });
            });
        },
        [clear, saveFields, guard],
    );

    /**
     * "Geen gebreken hier": alle antwoorden van dit hoofdonderdeel in één
     * transactie. Het plan wordt gemaakt op wat er op het moment van schrijven
     * bewaard staat, zodat een gebrek dat net getikt werd niet alsnog "Geen
     * gebreken" wordt.
     */
    const sweepCurrentNode = useCallback(async () => {
        if (!mainNodeId) {
            return;
        }

        await guard(surveyErrors.sweep, () =>
            inspectionMutations.saveAnswersFrom(
                inspectionUuid,
                (current) => {
                    const stored = answerSetFrom(current.answers, current.multiCounts);

                    return planPartSweep(
                        graph,
                        computeSnapshot(graph, stored),
                        stored,
                        mainNodeId,
                    ).entries.map((entry) => ({
                        inspectionUuid,
                        ...answerKeyToWire(entry.questionKey),
                        fields: {
                            optionIds: entry.answer.option_ids
                                ? [...entry.answer.option_ids]
                                : null,
                            valueNumber: entry.answer.value_number ?? null,
                            valueText: entry.answer.value_text ?? null,
                        },
                    }));
                },
                db,
            ),
        );
    }, [mainNodeId, graph, inspectionUuid, db, guard]);

    const setReportText = useCallback(
        async (key: AnswerKey, text: string) => {
            await guard(surveyErrors.note, () =>
                saveFields(key, { reportText: text.trim() === '' ? null : text.trim() }),
            );
        },
        [saveFields, guard],
    );

    /**
     * Herstelkosten: het bedrag komt in de kolom van de urgentie, de andere twee op nul.
     * Vraagt de keuze zelf om het bedrag, dan noemt ze de urgentie (`amount_prompt`).
     */
    const setFindingAmount = useCallback(
        async (key: AnswerKey, cents: number | null, prompted?: Urgency) => {
            const urgency = prompted ?? questionFindings.get(key)?.urgency ?? 'direct';

            await guard(surveyErrors.finding, () =>
                saveFields(key, repairCostFields(cents, urgency)),
            );
        },
        [questionFindings, saveFields, guard],
    );

    /** Een andere urgentie neemt een zelf ingevuld bedrag mee naar zijn kolom. */
    const setFindingUrgency = useCallback(
        async (key: AnswerKey, urgency: Urgency) => {
            const override = overrides.get(key);
            const amount = questionFindings.get(key)?.amountCents ?? null;
            const moved =
                override && hasCostOverride(override) ? repairCostFields(amount, urgency) : {};

            await guard(surveyErrors.finding, () => saveFields(key, { urgency, ...moved }));
        },
        [overrides, questionFindings, saveFields, guard],
    );

    /** Waar het gebrek zit: een handvol woorden, leeg maken mag. */
    const setFindingLocation = useCallback(
        async (key: AnswerKey, text: string) => {
            await guard(surveyErrors.location, () =>
                saveFields(key, { location: text.trim() === '' ? null : text.trim() }),
            );
        },
        [saveFields, guard],
    );

    const setFindingNhg = useCallback(
        async (key: AnswerKey, nhg: NhgClassification | null) => {
            await guard(surveyErrors.finding, () => saveFields(key, { nhgClassification: nhg }));
        },
        [saveFields, guard],
    );

    const setMultiCount = useCallback(
        async (nodeId: NodeId, count: number) => {
            const countKey = snapshot.tree.nodes.get(nodeId)?.multiCountKey;

            if (!countKey) {
                return;
            }

            await guard(surveyErrors.answer, () =>
                inspectionMutations.setMultiCount(
                    { inspectionUuid, ...multiCountKeyToWire(countKey), count },
                    db,
                ),
            );
        },
        [snapshot, inspectionUuid, db, guard],
    );

    /** Het bijschrift volgt het onderdeel waar de vraag staat, ook diep in de stroom. */
    const addPhotos = useCallback(
        async (view: QuestionView, files: readonly File[]) => {
            const runtime = snapshot.tree.nodes.get(view.nodeId);

            if (files.length === 0 || !runtime) {
                return;
            }

            await guard(surveyErrors.photo, () =>
                photoMutations.addToQuestion(
                    {
                        inspectionUuid,
                        ...answerKeyToWire(view.key),
                        files,
                        captionBase: runtime.name.toLowerCase(),
                        existingCount: photosByQuestion.get(view.key)?.length ?? 0,
                    },
                    db,
                ),
            );
        },
        [snapshot, inspectionUuid, photosByQuestion, db, guard],
    );

    const readOnly = isSubmitted(inspection);
    const completion = useMemo(
        () =>
            completionReport(
                snapshot,
                new Map([...photosByQuestion].map(([key, photos]) => [key, photos.length])),
            ),
        [snapshot, photosByQuestion],
    );
    /**
     * Verplichte foto's die op dit apparaat staan maar nog niet op de server: ze
     * tellen hierboven als aanwezig, en komen na het indienen alsnog binnen.
     */
    const requiredPhotosOnTheWay = useMemo(() => {
        const uploadedOnly = completionReport(
            snapshot,
            new Map(
                [...photosByQuestion].map(([key, photos]) => [
                    key,
                    photos.filter((photo) => photo.state === 'uploaded').length,
                ]),
            ),
        );

        return Math.max(0, uploadedOnly.photosMissing - completion.photosMissing);
    }, [snapshot, photosByQuestion, completion]);
    const [submission, setSubmission] = useState<{
        busy: 'complete' | 'submit' | null;
        error: string | null;
    }>({ busy: null, error: null });

    /** Afronden: lokaal en in de outbox, ook zonder verbinding. */
    const completeInspection = useCallback(async () => {
        setSubmission((current) => ({ ...current, busy: 'complete', error: null }));

        try {
            await inspectionMutations.complete(inspectionUuid, db);
            setSubmission((current) => ({ ...current, busy: null }));
        } catch (cause) {
            console.error('[opname] afronden mislukt', cause);
            setSubmission((current) => ({
                ...current,
                busy: null,
                error: surveyCopy.completion.completeFailed,
            }));
        }
    }, [inspectionUuid, db]);

    /**
     * Indienen: alleen online, en nooit tegengehouden door wat open staat - dat
     * komt niet in het rapport. Foto's die nog onderweg zijn, gaan erna mee.
     */
    const submitInspection = useCallback(async () => {
        setSubmission((current) => ({ ...current, busy: 'submit', error: null }));

        try {
            const submitted = await inspectionApi.submit(inspectionUuid);

            await inspectionMutations.markSubmitted(
                inspectionUuid,
                { completedAt: submitted.completed_at, submittedAt: submitted.submitted_at },
                db,
            );
            setSubmission({ busy: null, error: null });
        } catch (cause) {
            const error =
                isApiError(cause) && ['offline', 'network', 'timeout'].includes(cause.kind)
                    ? surveyCopy.completion.submitOffline
                    : isApiError(cause) && cause.status === 403
                      ? surveyCopy.completion.submitForbidden
                      : surveyCopy.completion.submitFailed;

            if (!isApiError(cause)) {
                console.error('[opname] indienen mislukt', cause);
            }

            setSubmission({ busy: null, error });
        }
    }, [inspectionUuid, db]);

    /** De onderdelen waar een handmatige bevinding bij kan horen, in boomvolgorde, op knoop-id. */
    const places = useMemo(
        () =>
            rows
                .filter((row) => !row.isSection)
                .map((row) => ({ value: row.nodeId, label: `${row.number} ${row.name}` })),
        [rows],
    );

    /**
     * Een handmatige bevinding gaat met haar onderdeel over de draad (`node_id` plus
     * `multi_scope`), zodat de API haar bedragen in dat onderdeel telt en het rapport
     * haar onder die kop zet. De titel is de naam van het onderdeel. Blijft het veld op
     * een onderdeel dat er niet meer is, dan houdt de bevinding haar oude plek.
     */
    const saveManualFinding = useCallback(
        async (findingUuid: string, draft: ManualFindingDraft) => {
            const runtime = draft.place ? snapshot.tree.nodes.get(draft.place) : undefined;
            const existing = data.manualFindings.find(
                (finding) => finding.findingUuid === findingUuid,
            );
            const address = runtime
                ? nodeAddressToWire(runtime)
                : {
                      nodeId: existing?.nodeId ?? null,
                      multiScope: [...(existing?.multiScope ?? [])],
                  };

            await guard(surveyErrors.manualFinding, () =>
                findingMutations.saveManual(
                    {
                        inspectionUuid,
                        findingUuid,
                        nodeId: address.nodeId,
                        multiScope: address.multiScope,
                        title:
                            runtime?.displayName ?? existing?.title ?? surveyCopy.findings.noPlace,
                        description: draft.description,
                        location: draft.location === '' ? null : draft.location,
                        severity: draft.severity,
                        urgency: manualUrgency(draft.severity, draft.costs),
                        directCost: centsToAmount(draft.costs.direct),
                        termCost: centsToAmount(draft.costs.term),
                        improvementCost: centsToAmount(draft.costs.improvement),
                        costGroupId: null,
                        nhgClassification: null,
                    },
                    db,
                ),
            );
        },
        [snapshot, data.manualFindings, inspectionUuid, db, guard],
    );

    const removeManualFinding = useCallback(
        async (findingUuid: string) => {
            await guard(surveyErrors.manualFinding, () =>
                findingMutations.removeManual({ inspectionUuid, findingUuid }, db),
            );
        },
        [inspectionUuid, db, guard],
    );

    /**
     * Foto's bij een handmatige bevinding, ook bij een die nog niet is opgeslagen: ze
     * wachten dan lokaal tot de bevinding op de server staat. Het bijschrift volgt
     * het onderdeel, net als onder een vraag ("voegwerk 2").
     */
    const addFindingPhotos = useCallback(
        async (findingUuid: string, files: readonly File[]) => {
            const existing = data.manualFindings.find(
                (finding) => finding.findingUuid === findingUuid,
            );

            await guard(surveyErrors.photo, () =>
                photoMutations.addToFinding(
                    {
                        inspectionUuid,
                        findingUuid,
                        files,
                        captionBase: (
                            existing?.title ?? surveyCopy.manual.photoCaption
                        ).toLowerCase(),
                        existingCount: findingPhotos.get(findingUuid)?.length ?? 0,
                    },
                    db,
                ),
            );
        },
        [data.manualFindings, inspectionUuid, findingPhotos, db, guard],
    );

    const discardFindingPhotos = useCallback(
        async (findingUuid: string) => {
            await guard(surveyErrors.photoRemove, () =>
                photoMutations.discardForFinding({ inspectionUuid, findingUuid }, db),
            );
        },
        [inspectionUuid, db, guard],
    );

    const removePhoto = useCallback(
        async (clientUuid: string) => {
            await guard(surveyErrors.photoRemove, () => photoMutations.remove(clientUuid, db));
        },
        [db, guard],
    );

    const dismissConflicts = useCallback(
        async (ids: readonly string[]) => {
            await acknowledgeConflicts(db, ids);
        },
        [db],
    );

    return {
        graph,
        snapshot,
        object,
        buildYear,
        answers,
        notes,
        rows,
        stream,
        meta,
        position,
        findings,
        questionFindings,
        costs,
        photosByQuestion,
        conflicts: data.conflicts,
        activeNodeId,
        activeRuntime,
        activeProgress,
        mainNodeId,
        mainRuntime,
        activePlace: activeNodeId ? placeOf(snapshot, activeNodeId) : '',
        sweepCount: sweepPlan.entries.length,
        hiddenQuestions: Math.max(
            0,
            graph.package.questions.length - snapshot.progress.visibleQuestions,
        ),
        readOnly,
        completion,
        requiredPhotosOnTheWay,
        submission,
        completeInspection,
        submitInspection,
        writeError,
        dismissWriteError: () => setWriteError(null),
        selectNode,
        goToNeighbour,
        goToFirstIncomplete,
        toggleOption,
        setValue,
        sweepCurrentNode,
        setReportText,
        setFindingAmount,
        setFindingUrgency,
        setFindingLocation,
        setFindingNhg,
        setMultiCount,
        addPhotos,
        removePhoto,
        places,
        saveManualFinding,
        removeManualFinding,
        findingPhotos,
        addFindingPhotos,
        discardFindingPhotos,
        newManualFindingUuid: newUlid,
        dismissConflicts,
    };
}

export type SurveyState = ReturnType<typeof useSurveyState>;
export type { FindingView, PackageQuestion, QuestionView };
